/**
 * /recurring-charges — the subscriptions, memberships, and auto-drafts that
 * hit the bank accounts on a regular cadence, so the owner can see at a glance
 * "what keeps pulling money out of here" and cut what they don't need.
 *
 * Source is Plaid's recurring streams when a bank is connected, else detected
 * from posted bank activity (see app/services/recurring_watch.py).
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { recurringChargesQueryOptions } from "@/lib/queries";
import type { RecurringCharge } from "@/lib/queries";

export const Route = createFileRoute("/_auth/recurring-charges")({
  component: RecurringChargesPage,
});

const money = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

function cadenceChip(cadence: string) {
  const c = "px-2 py-0.5 rounded-full text-[11px] font-medium ";
  return c + "bg-slate-100 text-slate-700 dark:bg-gray-800 dark:text-slate-300";
}

function RecurringChargesPage() {
  const { data, isLoading } = useQuery(recurringChargesQueryOptions());

  return (
    <div className="h-full overflow-y-auto bg-slate-50 dark:bg-gray-950 px-6 py-6">
      <div className="max-w-5xl mx-auto">
        <div className="mb-5">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Recurring charges</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Subscriptions, memberships, and auto-drafts that hit your accounts on a regular cadence.
          </p>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <Tile label="Monthly recurring" value={data ? money(data.monthly_recurring_total) : "—"} accent />
          <Tile label="Annualized" value={data ? money(data.annualized_total) : "—"} />
          <Tile label="Active charges" value={data ? String(data.active_count) : "—"} />
          <Tile
            label="Source"
            value={data ? (data.source === "plaid" ? "Bank feed" : "Ledger") : "—"}
          />
        </div>

        {data && data.source !== "plaid" && data.plaid_status === "not_ready" && (
          <div className="mb-4 px-4 py-2.5 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 text-sm text-amber-800 dark:text-amber-300">
            Your bank feed is still preparing recurring data (this takes a minute after connecting). Showing
            ledger-detected charges for now — refresh shortly for the full bank-feed view.
          </div>
        )}

        {isLoading && (
          <div className="rounded-xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 text-center text-sm text-slate-500">
            Scanning your accounts…
          </div>
        )}

        {data && data.charges.length === 0 && (
          <div className="rounded-xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 text-center text-sm text-slate-500 dark:text-slate-400">
            No recurring charges detected yet. Once a few months of bank activity flow in, repeating
            charges show up here automatically.
          </div>
        )}

        {data && data.charges.length > 0 && (
          <div className="rounded-xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 dark:text-slate-400 border-b border-slate-100 dark:border-gray-800">
                  <th className="px-4 py-2.5 font-medium">Merchant</th>
                  <th className="px-4 py-2.5 font-medium">Cadence</th>
                  <th className="px-4 py-2.5 font-medium text-right">Typical</th>
                  <th className="px-4 py-2.5 font-medium">Next expected</th>
                  <th className="px-4 py-2.5 font-medium text-right">Times</th>
                  <th className="px-4 py-2.5 font-medium text-right">Annualized</th>
                  <th className="px-4 py-2.5 font-medium">Account</th>
                </tr>
              </thead>
              <tbody>
                {data.charges.map((c: RecurringCharge, i: number) => (
                  <tr
                    key={`${c.merchant}-${i}`}
                    className={
                      "border-b border-slate-50 dark:border-gray-800/50 " +
                      (c.active ? "" : "opacity-50")
                    }
                  >
                    <td className="px-4 py-2.5 text-slate-800 dark:text-slate-200">
                      {c.merchant}
                      {!c.active && (
                        <span className="ml-2 text-[11px] text-slate-400">(stopped)</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={cadenceChip(c.cadence)}>{c.cadence}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-700 dark:text-slate-300">
                      {money(c.typical_amount)}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400">{fmtDate(c.predicted_next)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-600 dark:text-slate-400">{c.occurrences}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium text-slate-800 dark:text-slate-200">
                      {money(c.annualized)}
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 dark:text-slate-400">{c.account}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div
      className={
        "rounded-xl border p-4 " +
        (accent
          ? "border-blue-200 bg-blue-50 dark:border-blue-900/50 dark:bg-blue-950/30"
          : "border-slate-200 bg-white dark:border-gray-800 dark:bg-gray-900")
      }
    >
      <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
      <div className="text-lg font-semibold text-slate-900 dark:text-slate-100 mt-0.5">{value}</div>
    </div>
  );
}
